import { useState } from 'react';
import {
  Button, Chip, Stack, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Alert, Snackbar, IconButton, Tooltip, Table, TableHead, TableBody, TableRow, TableCell, Box, CircularProgress, Divider,
} from '@mui/material';
import { Add, UploadFile, Delete, Visibility } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { FileUploadField } from '../../components/FileUploadField';
import { priceListsApi, type ManufacturerPriceList, type PriceListRow } from '../../api/boqMasters.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n?: number | null) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);
const emptyRow = (): PriceListRow => ({ materialCode: '', name: '', category: '', hsnCode: '', brand: '', description: '', unit: '', mrp: null, discountPercent: null, rate: null });

export function ManufacturerPriceListsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['price-lists', page, limit], queryFn: () => priceListsApi.list({ page, limit }) });
  const canCreate = hasPermission('rate_card.create');

  const columns: Column<ManufacturerPriceList>[] = [
    { key: 'code', label: 'Code' },
    { key: 'manufacturer', label: 'Manufacturer', render: (r) => <Chip size="small" label={r.manufacturer} /> },
    { key: 'title', label: 'Title' },
    { key: 'effectiveDate', label: 'Effective', render: (r) => (r.effectiveDate ? new Date(r.effectiveDate).toLocaleDateString('en-IN') : '—') },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color="success" label={r.status} /> },
    { key: 'actions', label: '', width: 60, render: (r) => <Tooltip title="View"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip> },
  ];

  return (
    <>
      <PageHeader title="Manufacturer Price Lists" subtitle="Upload a manufacturer/supplier catalogue (Polycab, KEI, Anchor, L&T, Havells…) → a separate rate card of material code, name, brand, description & rates."
        action={canCreate && <Button startIcon={<Add />} variant="contained" onClick={() => setOpen(true)}>Upload Price List</Button>} />
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id} />

      {open && <UploadDialog onClose={() => setOpen(false)} onDone={(code) => { setOpen(false); setToast(`Price list ${code} created`); qc.invalidateQueries({ queryKey: ['price-lists'] }); }} />}
      {detailId && <DetailDialog id={detailId} onClose={() => setDetailId(null)} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function UploadDialog({ onClose, onDone }: { onClose: () => void; onDone: (code: string) => void }) {
  const [form, setForm] = useState({ manufacturer: '', title: '', effectiveDate: '', fileUrl: '' });
  const [rows, setRows] = useState<PriceListRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const setRow = (i: number, k: keyof PriceListRow, v: unknown) => setRows((arr) => arr.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const parseMut = useMutation({
    mutationFn: () => priceListsApi.parse(form.fileUrl),
    onSuccess: (r) => {
      setError(null);
      if (r.configured === false) { setInfo(r.message ?? 'AI not configured — upload Excel/CSV or enter manually.'); return; }
      if (!r.rows.length) { setInfo(r.message ?? 'No items detected — check the file.'); setRows([]); return; }
      const cols = Object.values(r.detectedColumns).join(', ');
      const base = `Parsed ${r.rows.length} item(s)${r.source === 'ai' ? ' (AI)' : cols ? ` — mapped: ${cols}` : ''}. Review & edit below.`;
      setInfo(r.message ? `${base} ⚠ ${r.message}` : base); // surface truncation/partial warnings
      setRows(r.rows.map((x) => ({ ...emptyRow(), ...x })));
      if (r.manufacturer && !form.manufacturer) setForm((f) => ({ ...f, manufacturer: r.manufacturer as string }));
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const saveMut = useMutation({
    mutationFn: () => priceListsApi.create({
      manufacturer: form.manufacturer.trim(), title: form.title.trim(), effectiveDate: form.effectiveDate || undefined,
      rows: rows.filter((r) => (r.name ?? '').trim() || (r.materialCode ?? '').trim()).map((r) => ({
        ...r, mrp: r.mrp != null ? Number(r.mrp) : null, discountPercent: r.discountPercent != null ? Number(r.discountPercent) : null, rate: r.rate != null ? Number(r.rate) : null,
      })),
    }),
    onSuccess: (s) => onDone(s.code), onError: (e) => setError(extractApiError(e)),
  });

  const validRows = rows.filter((r) => (r.name ?? '').trim() || (r.materialCode ?? '').trim());
  const canParse = Boolean(form.fileUrl) && !parseMut.isPending;
  const canSave = form.manufacturer.trim().length > 0 && form.title.trim().length >= 2 && validRows.length > 0 && !saveMut.isPending;

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>Upload Manufacturer Price List</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Manufacturer *" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} sx={{ minWidth: 200 }} helperText="e.g. Polycab, KEI, L&T" />
            <TextField size="small" label="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ minWidth: 260 }} helperText="e.g. Polycab LT Cables FY26-27" />
            <TextField size="small" type="date" label="Effective Date" InputLabelProps={{ shrink: true }} value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} />
          </Stack>

          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <FileUploadField label="Price list (Excel/CSV or PDF/image)" accept=".xlsx,.xls,.csv,.pdf,image/*" value={form.fileUrl} onChange={(url) => setForm({ ...form, fileUrl: url ?? '' })} />
              </Box>
              <Button startIcon={parseMut.isPending ? <CircularProgress size={16} /> : <UploadFile />} variant="outlined" disabled={!canParse} onClick={() => { setInfo(null); parseMut.mutate(); }}>
                Parse file
              </Button>
            </Stack>
            {info && <Alert severity="info" sx={{ mt: 1, py: 0 }}>{info}</Alert>}
          </Box>

          {rows.length > 0 && (
            <>
              <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Items (review & edit) — {validRows.length}</Typography></Divider>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Category</TableCell><TableCell>HSN</TableCell><TableCell>Material Code</TableCell><TableCell>Name *</TableCell><TableCell>Brand</TableCell><TableCell>Description</TableCell>
                  <TableCell>Unit</TableCell><TableCell align="right">MRP</TableCell><TableCell align="right">Disc%</TableCell><TableCell align="right">Rate</TableCell><TableCell />
                </TableRow></TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell><TextField size="small" variant="standard" value={r.category ?? ''} onChange={(e) => setRow(i, 'category', e.target.value)} sx={{ width: 120 }} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" value={r.hsnCode ?? ''} onChange={(e) => setRow(i, 'hsnCode', e.target.value)} sx={{ width: 80 }} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" value={r.materialCode ?? ''} onChange={(e) => setRow(i, 'materialCode', e.target.value)} sx={{ width: 110 }} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" fullWidth value={r.name} onChange={(e) => setRow(i, 'name', e.target.value)} sx={{ minWidth: 160 }} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" value={r.brand ?? ''} onChange={(e) => setRow(i, 'brand', e.target.value)} sx={{ width: 90 }} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" fullWidth value={r.description ?? ''} onChange={(e) => setRow(i, 'description', e.target.value)} sx={{ minWidth: 180 }} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" value={r.unit ?? ''} onChange={(e) => setRow(i, 'unit', e.target.value)} sx={{ width: 56 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={r.mrp ?? ''} onChange={(e) => setRow(i, 'mrp', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 80 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={r.discountPercent ?? ''} onChange={(e) => setRow(i, 'discountPercent', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 56 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={r.rate ?? ''} onChange={(e) => setRow(i, 'rate', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 80 }} /></TableCell>
                      <TableCell><IconButton size="small" color="error" onClick={() => setRows((arr) => arr.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button size="small" startIcon={<Add />} onClick={() => setRows((arr) => [...arr, emptyRow()])}>Add item</Button>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={() => saveMut.mutate()}>Create Price List</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const { data: p } = useQuery({ queryKey: ['price-list', id], queryFn: () => priceListsApi.get(id) });
  if (!p) return null;
  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>{p.code} — {p.manufacturer}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {p.title}{p.effectiveDate ? ` · Effective ${new Date(p.effectiveDate).toLocaleDateString('en-IN')}` : ''} · {p.items?.length ?? 0} items
        </Typography>
        <Table size="small">
          <TableHead><TableRow><TableCell>Category</TableCell><TableCell>HSN</TableCell><TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>Brand</TableCell><TableCell>Unit</TableCell><TableCell align="right">MRP</TableCell><TableCell align="right">Disc%</TableCell><TableCell align="right">Rate</TableCell></TableRow></TableHead>
          <TableBody>
            {(p.items ?? []).map((i) => (
              <TableRow key={i.id}>
                <TableCell>{i.category ?? '—'}</TableCell><TableCell>{i.hsnCode ?? '—'}</TableCell><TableCell>{i.materialCode ?? '—'}</TableCell><TableCell>{i.name}</TableCell><TableCell>{i.brand ?? '—'}</TableCell>
                <TableCell>{i.unit ?? '—'}</TableCell>
                <TableCell align="right">{inr(i.mrp)}</TableCell><TableCell align="right">{i.discountPercent ?? '—'}</TableCell><TableCell align="right">{inr(i.rate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
